import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Button, Skeleton } from "@chakra-ui/react";
import { useSession } from "./session";
import { FiMessageCircle, FiBookmark } from "react-icons/fi";
import useDocumentMeta, { SITE_NAME } from "./seo";

export default function CharacterDetailPage() {
  const { publicId } = useParams();
  const { api, favorites, favoriteBusy, toggleFavorite, auth } = useSession();
  const location = useLocation(),
    from = location.state?.from || "/";
  const [card, setCard] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    const abort = new AbortController();
    setCard(null);
    setError("");
    api(`/api/chara/${publicId}`, { signal: abort.signal })
      .then((response) => response.json())
      .then(setCard)
      .catch((err) => {
        if (err.name !== "AbortError") setError(err.message);
      });
    return () => abort.abort();
  }, [api, publicId]);
  const summary = (card?.description || card?.persona || "")
    .replace(/\s+/g, " ")
    .trim();
  useDocumentMeta(
    card
      ? {
          title: `${Array.from(card.name).slice(0, 60).join("")} - AI Character Chat | ${SITE_NAME}`,
          description:
            Array.from(summary).length > 155
              ? Array.from(summary).slice(0, 154).join("").trimEnd() + "…"
              : summary || `Chat with ${card.name}, an AI character on ${SITE_NAME}.`,
          canonicalPath: `/character/${publicId}`,
        }
      : error
        ? {
            title: `Character unavailable - ${SITE_NAME}`,
            canonicalPath: "/",
            noindex: true,
          }
        : null,
  );
  return (
    <div className="catalog-detail">
      <Button as={Link} to={from} variant="ghost" mb={6}>
        ← Back to characters
      </Button>
      {error ? (
        <div className="empty-state" role="alert">
          <h1>Character unavailable</h1>
          <p>{error}</p>
        </div>
      ) : !card ? (
        <Skeleton height="500px" borderRadius="16px" />
      ) : (
        <article className="catalog-detail-card">
          <img src={card.image} alt={card.name} width="832" height="1216" />
          <div>
            <h1>{card.name}</h1>
            <p>{card.description}</p>
            {!card.catalogOnly && <div className="detail-actions">
              <Button
                as={Link}
                to={`/chat/${publicId}`}
                state={{ from }}
                variant="brand"
                leftIcon={<FiMessageCircle />}
              >
                Start chat
              </Button>
              <Button
                variant="outline"
                isDisabled={auth.isLoading}
                isLoading={favoriteBusy.has(publicId)}
                aria-pressed={favorites.has(publicId)}
                leftIcon={<FiBookmark />}
                onClick={() => toggleFavorite(publicId)}
              >
                {favorites.has(publicId) ? "Saved" : "Save character"}
              </Button>
            </div>}
            <div className="character-tags">
              {card.tags?.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
            {card.persona && (
              <section>
                <h2>Character</h2>
                <p>{card.persona}</p>
              </section>
            )}
            {card.scenario && (
              <section>
                <h2>Scenario</h2>
                <p>{card.scenario}</p>
              </section>
            )}
            {card.greeting && (
              <section>
                <h2>Introduction</h2>
                <p>{card.greeting}</p>
              </section>
            )}
            {card.exampleDialogue && (
              <section>
                <h2>Example conversation</h2>
                <p>{card.exampleDialogue}</p>
              </section>
            )}
          </div>
        </article>
      )}
      {card && !error && !card.catalogOnly && (
        <div className="detail-mobile-cta">
          <Button
            as={Link}
            to={`/chat/${publicId}`}
            state={{ from }}
            variant="brand"
            width="full"
            leftIcon={<FiMessageCircle />}
          >
            Start chat
          </Button>
        </div>
      )}
    </div>
  );
}
